import * as React from 'react';
import {useState, useEffect} from 'react';
import {difference, indexBy, has, groupBy, mapObjIndexed} from 'ramda';
import {Panel} from 'react-popup-context';
import {date, time as Time} from 'ka-erp-data';
import DataLoader from 'dataloader';
import Main from '../index/main';
import OrderEditor from './order-editor';
import {Plus} from '../common/btns';
import * as api from '../api';
import {assert, time} from '../../lib/utils';

const loader = {
    orderItem: new DataLoader(async (_ids: readonly number[]) => {
        const res = await api.orderItem.get(
            ['_id', 'seq', 'product', 'qty', 'classCnt', 'giftQty', 'memo'],
            _ids);
        const d = groupBy(i => i._id.toString(), res);
        return _ids.map(id => has(id.toString(), d) ? d[id.toString()] : []);
    }),
    product: new DataLoader(async (ids: readonly string[]) => {
        const products = await api.product.get(ids, ['id', 'name']);
        const d = indexBy(i => i.id, products);
        return ids.map(id => has(id, d) ? d[id] : null);
    }),
};

const css = require('./app.css').default;

export default function ({dealer}: {
    dealer: string,
}) {
    const [receivers, setReceivers] = useState<receiver[]>([]);
    const [orders, setOrders] = useState<order[] | null>(null);
    const [onlineProducts, setOnlineProducts] = useState<product[] | null>(null);

    const now = Date.now();
    const start = date.fromDate(new Date(now - (7 * time.day)));
    const end = date.fromDate(new Date(now + 1 * time.day));

    useEffect(() => {
        api.product.online(['id', 'name', 'code', 'memo'], '金安')
        .then(setOnlineProducts);
    }, []);

    useEffect(() => {
        api.dealer.receiver(dealer, ['id', 'name', 'address'])
        .then(setReceivers);
    }, []);

    useEffect(() => {
        loadOrders(dealer, start, end).then(setOrders);
    }, []);

    useEffect(() => {
        if (orders !== null) {
            api.pkg.get(orders.map(o => o.id), ['order', 'logistic'], ['packDate'])
            .then(pkgs => {
                const pkgsByOrd = groupBy(i => i.order, pkgs);
                setOrders(orders.map(ord => {
                    const pkgs = get(pkgsByOrd, ord.id) || [];
                    return {...ord, pkgs};
                }));
            });
        }
    }, [orders === null ? null : orders.map(o => o.id).join(',')])

    return (
    <Panel width='100%' height='100%'>
        {ctx => {
            return (
            <>
                <Main ctx={ctx} dealer={dealer} orders={orders}/>
                <div className={css.createOrder}>
                    {(() => {
                        if (onlineProducts !== null) {
                            return <Plus width={50} height={50} color='white' onClick={() => {
                                ctx.popup(<OrderEditor
                                    ctx={ctx} receivers={receivers}
                                    products={onlineProducts}
                                    onClose={() => ctx.close()}
                                    onDone={async ord => {
                                        assert(onlineProducts !== null, 'products is null');
                                        const rules = await api.freebieRule.load();
                                        const productByCode = indexBy(p => p.code, onlineProducts);
                                        const productIdByCode = mapObjIndexed(p => p.id, productByCode);
                                        const items = ord.items
                                        .map(i => ({
                                            code: i.product.code,
                                            ...i,
                                        }));
                                        await api.order.post(dealer, ord.receiver, ord.deliverDate, ord.memo, items, rules, productIdByCode);
                                        setOrders(await loadOrders(dealer, start, end));
                                        ctx.close();
                                    }}/>);
                            }}/>
                        } else {
                            return <></>;
                        }
                    })()}
                </div>
            </>);
        }}
    </Panel>);
}

async function loadOrders(dealer: string, start: date, end: date): Promise<order[]> {
    const ords = (await getOrderHeads())
    .map(async ord => ({
        ...ord,
        items: await loader.orderItem.load(ord._id),
    }))
    .map(async _ord => {
        const ord = await _ord;
        return {
            ...ord,
            items: await Promise.all(ord.items.map(async item => ({
                ...item,
                product: (await loader.product.load(item.product))!,
            }))),
        }
    });

    return (await Promise.all(ords))
    .sort((a, b) => {
        return datetime2num(b.createDate, b.createTime) - datetime2num(a.createDate, a.createTime);
    });
    
    async function getOrderHeads() {
        const fs = ['id', '_id', 'createDate', 'createTime', 'billDate', 'deliverDate', 'memo', 'receiverId', 'receiverName', 'address'] as const;
        const oips = await api.orderItemPending.getPending(['oriOrder'], dealer);
        const outOfStocks = new Set(oips.map(i => i.oriOrder));
        const ords = await api.order.getByDateRange(fs, dealer, start, end);
        const missings = difference(oips.map(i => i.oriOrder), ords.map(i => i.id));
        const supplements = await api.order.get(fs, missings);
        return ords
        .concat(supplements)
        .map(o => ({
            ...o,
            outOfStock: outOfStocks.has(o.id),
            pkgs: 'loading' as const,
        }));
    }
}

function datetime2num(_date: date, _time: Time) {
    return parseInt(date.toNum(_date).toString() + Time.toNum(_time).toString().padStart(6, '0'));
}

function get<data>(dict: {[key: string]: data}, key: string): data | undefined {
    return dict[key];
}

type receiver = {
    id: string,
    name: string | null,
    address: string,
}

type product = {
    id: string,
    code: string,
    name: string,
    memo: string | null,
}

type order = {
    id: string,
    _id: number,
    createDate: date,           // 建立日期
    billDate: date,
    deliverDate: date | null,   // 預計出貨日
    receiverId: string | null,
    receiverName: string | null,
    address: string,
    memo: string | null,
    outOfStock: boolean,
    pkgs: pkg[] | 'loading',
    items: {
        _id: number,
        seq: number,
        product: {
            id: string,
            name: string,
        } | 'loading',
        qty: number,
        classCnt: number,
        giftQty: number | null,
        memo: string | null,
    }[],
}

type pkg = {
    order: string,
    logistic: string,
    items: {
        packDate: date | null,
    }[],
}
